import { parseUrl } from '@/utils/url';
import { ls } from '@sfe/bone-cache';

export function toArray(arr, { ignoreNull = false } = {}) {
    if (ignoreNull && (arr === null || arr === undefined)) return [];
    return Array.isArray(arr) ? arr : [arr];
}

export function debounce(cb, delay = 100) {
    let timer;
    return function callback() {
        clearTimeout(timer);
        // eslint-disable-next-line prefer-rest-params
        const args = arguments;
        const context = this;
        timer = setTimeout(() => {
            cb.apply(context, args);
        }, delay);
    };
}

/**
 * 格式化精度，默认值为 `0`
 *
 * @param {string} value 后台配置的精度原始字段，如：0.0001 、0.1 、0
 * @return {number}
 */
export function formatPrecision(value) {
    if (typeof value !== 'string' || value.indexOf('.') === -1) return 0;
    const split = value.split('.');
    return split[1] ? split[1].length : 0;
}

/**
   * 观察元素resize变化
   *
   * @param {element} el 监听的dom元素
   * @param {Function} cb 监听的函数
   */
export const resizeObserver = (el, cb) => {
    if (!el) throw new Error('el 不能为空');

    const observer = new ResizeObserver(cb);
    observer.observe(el);

    return () => {
        observer.unobserve(el);
    };
};

/**
 * 全局替换数值中的全角数字、符号，注意：只替换不会删除字符
 *
 * @param {(string|number)} param 数值
 * @return {string}
 *
 * @example
 * toEnNumber('123０。．'); => 1230..
 * toEnNumber('。,，．'); => .,,.
 */
export function toEnNumber(param) {
    const mapping = {
        '０': 0,
        '１': 1,
        '２': 2,
        '３': 3,
        '４': 4,
        '５': 5,
        '６': 6,
        '７': 7,
        '８': 8,
        '９': 9,
        '。': '.',
        '．': '.',
    };

    const reg = new RegExp(`[${Object.keys(mapping).join('')}]`, 'gi');
    return param.toString().replace(reg, (matched) => mapping[matched]);
}

/**
 * 空闲执行回调
 *
 * @param {Function} callback 回调
 * @return {number}
 */
export function requestIdleCallback(callback) {
    return window.requestIdleCallback
        ? window.requestIdleCallback(callback)
        : setTimeout(callback, 1);
}

/**
 * onload 事件
 *
 * @return {Promise}
 */
export function onload() {
    if (document.readyState === 'complete') {
        return Promise.resolve();
    }
    return new Promise((done) => {
        window.addEventListener('load', () => {
            done();
        }, false);
    });
}

/**
 * 统一定义一个加载异步组件的时机，用于减少首屏加载，但又想在用户操作前预加载
 *
 * @return {Promise}
 */
export function loadAsyncComponent() {
    if (window.sfe.prerender) return new Promise((done) => done); // 预渲染时不加载异步组件
    return new Promise((done) => {
        onload().then(() => {
            requestIdleCallback(() => done());
        });
    });
}

/**
* 去除重复数组
*
* @param {string} key 关键id
* @param {Array} ar  数组
* @param {string} time  排序
* @return {Array}
*/
export const excludeRepeatArray = (key, ar, time) => {
    const obj = {};
    if (!key || !ar || Object.prototype.toString.call(ar) !== '[object Array]') { return null; }
    ar.forEach((item) => {
        if (obj[item[key]]) {
            // 如果重复，保留time最新的数据
            if (time && item[time] - obj[item[key]][time] >= 0) {
                obj[item[key]] = item;
            }
        } else {
            obj[item[key]] = item;
        }
    });
    return Object.values(obj).sort((a, b) => (a.time - b.time >= 0 ? -1 : 1));
};

/**
 *
 * 动态创建script
 *
 * @param {Object} options 参数
 * @param {string} options.id 唯一id必传
 * @param {string} options.src script src 必传
 * @param {boolean} isAfterDom 是否插入到head第一个元素
 * @return {promise} 如果创建成功返回创建的script dom 失败返回 false
 */
export const loadJs = (options, isAfterDom) => {
    if (!options.id || !options.src) return Promise.resolve(false);

    let scriptDom = document.querySelector(`script#${options.id}`);

    const scriptReady = (resolve) => {
        // 兼容ie
        if (scriptDom.readyState) {
            scriptDom.onreadystatechange = () => {
                if (scriptDom.readyState === 'loaded' || scriptDom.readyState === 'complete') {
                    scriptDom.onreadystatechange = null;
                    resolve(scriptDom);
                }
            };
        } else {
            scriptDom.onload = () => resolve(scriptDom);
        }
    };

    return new Promise((resolve) => {
        // 如果有script直接返回处理
        if (scriptDom) return scriptReady(resolve);
        if (!scriptDom) {
            scriptDom = document.createElement('script');
            const attrs = {
                type: 'text/javascript',
                async: true,
                // crossorigin: 'anonymous', // 解决火狐跨域问题
                ...options,
            };

            if (options.async === false) {
                delete attrs.async;
            }

            Object.entries(attrs).forEach(([key, value]) => {
                scriptDom.setAttribute(key, value);
            });
        }

        // 监听scriptDom ready
        scriptReady(resolve);

        scriptDom.src = options.src;
        if (isAfterDom === true) {
            document.getElementsByTagName('body')[0].appendChild(scriptDom);
        } else {
            document.getElementsByTagName('head')[0].appendChild(scriptDom);
        }
        return scriptDom;
    });
};

/**
 * 倒计时
 *
 * @param {number} start 开始时间
 * @param {number} end 结束时间
 */
export const countdown = (start, end) => {
    let diff = end - start;

    diff = diff > 0 ? diff : 0;

    const d = parseInt(diff / (24 * 60 * 60 * 1000), 10);
    const h = parseInt((diff / (60 * 60 * 1000)) % 24, 10);
    const m = parseInt((diff / (60 * 1000)) % 60, 10);
    const s = parseInt((diff / 1000) % 60, 10);
    const ms = parseInt(diff % 1000, 10);

    return {
        d: `0${d}`.slice(-Math.max(`${d}`.length, 2)),
        h: `0${h}`.slice(-2),
        m: `0${m}`.slice(-2),
        s: `0${s}`.slice(-2),
        ms: `00${ms}`.slice(-3),
    };
};

/**
 * 版本号对比函数
 *
 * @param {string} v1 目标版本号
 * @param {string} v2 对比版本号
 * @return {number} (v1 > v2) 返回 1; (v1 < v2) 返回 -1; (v1 === v2) 返回 0;
 * @example
 * compareVersion(1.0.1, 1.0.0) return 1;
 * compareVersion(1.9.10, 1.10.0) return -1;
 * compareVersion(1.9.0.0, 1.9) return 0;
 */
export const compareVersion = (v1, v2) => {
    const v1Arr = v1.split('.');
    const v2Arr = v2.split('.');
    const len = Math.max(v1Arr.length, v2Arr.length);

    for (let i = 0; i < len; i += 1) {
        const v1Item = Number(v1Arr[i]) || 0;
        const v2Item = Number(v2Arr[i]) || 0;
        if (v1Item > v2Item) {
            return 1;
        }
        if (v1Item < v2Item) {
            return -1;
        }
    }

    return 0;
};

export const isObject = (obj) => Object.prototype.toString.call(obj) === '[object Object]';

export const isPC = () => {
    const ua = navigator.userAgent;
    const isAndroid = /(?:Android)/.test(ua);
    const isFireFox = /(?:Firefox)/.test(ua);
    const isTablet = /(?:iPad|PlayBook)/.test(ua) || (isAndroid && !/(?:Mobile)/.test(ua)) || (isFireFox && /(?:Tablet)/.test(ua));
    const isIPhone = /(?:iPhone)/.test(ua) && !isTablet;

    return !isIPhone && !isAndroid;
};

export const referrerPath = () => {
    if (!document.referrer) return '';
    return parseUrl(document.referrer).pathname;
};

// 判断值是否为非空值（用于数值展示判断）
export const hasValue = (value) => !!value || value === 0;

/**
 * 兼容 获取json格式缓存兼容报错和结果不是对象
 *
 * @param {string} key 获取的localStorage key
 * @return {Object} 缓存对象，如果获取缓存不是对象或者出错返回空对象
 *
 */
export const lsGetJson = (key) => {
    let tmp = {};
    if (!key) {
        return tmp;
    }

    try {
        const cache = ls.getJSON(key);

        tmp = isObject(cache) ? cache : {};
    } catch (error) {
        // todo::
    }

    return tmp;
};

/**
 * 格式化精度，默认值为 `0`
 *
 * @param {string} value 后台配置的精度原始字段，如：1, 2, 3
 * @return {number} 0.1 0.01 0.001
 */
export function toPrecision(value) {
    const val = Number(value);
    if (Number.isNaN(val)) return '0';
    return `0.${'0'.repeat(val - 1)}1`;
}

/**
 * parse json string without throwing error
 *
 * @param {string } str JSON string
 * @param {any} defaultValue default Value
 */
export function safeJsonParse(str, defaultValue) {
    try {
        return JSON.parse(str);
    } catch {
        return defaultValue;
    }
}

/**
 * 格式化为0
 *
 * @param {any} str 需要格式化为0
 * toNumberZero('9.88') // '9.88'
 * toNumberZero('0.000000') // 0
 * toNumberZero('0.000000001') // '0.000000001'
 *
 */
export function toNumberZero(str) {
    return Number(str) === 0 ? 0 : str;
}
